import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';

// Firebase Auth para Contenido Generator (usa proyecto weather-hub-20289)
const firebaseConfig = {
  apiKey: "AIzaSyAV-tm8twLLNTrU96KIkBsrQGsHqAe4ziA",
  authDomain: "auth.contenido.sembitools.com",
  projectId: "weather-hub-20289",
  storageBucket: "weather-hub-20289.firebasestorage.app",
  messagingSenderId: "430899864321",
  appId: "1:430899864321:web:f30af2ea31cc69226eb100"
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

const googleProvider = new GoogleAuthProvider();

export interface AuthResult {
  user: User;
  isAdmin: boolean;
}

/**
 * Verificar whitelist en backend después del login
 */
async function verifyAndGetAccess(user: User): Promise<AuthResult> {
  const token = await user.getIdToken();

  const response = await fetch('/api/firebase-auth/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token })
  });

  const data = await response.json();

  if (!response.ok) {
    if (data.error === 'Usuario no autorizado') {
      // Crear solicitud de acceso
      await fetch('/api/firebase-auth/request-access-google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.displayName || user.email?.split('@')[0],
          googleUid: user.uid
        })
      });

      await signOut(auth);
      throw new Error('NEEDS_APPROVAL');
    }
    await signOut(auth);
    throw new Error(data.error || 'Error de autenticación');
  }

  return {
    user,
    isAdmin: data.user?.isAdmin || false
  };
}

/**
 * Login con Google
 */
export async function loginWithGoogle(): Promise<AuthResult> {
  const result = await signInWithPopup(auth, googleProvider);
  return verifyAndGetAccess(result.user);
}

/**
 * Login con Email/Password
 */
export async function loginWithEmail(email: string, password: string): Promise<AuthResult> {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return verifyAndGetAccess(result.user);
}

/**
 * Registrar nuevo usuario con Email/Password
 * Crea el usuario en Firebase Auth y solicita acceso
 */
export async function registerWithEmail(email: string, password: string, name: string): Promise<void> {
  // Crear usuario en Firebase Auth
  const result = await createUserWithEmailAndPassword(auth, email, password);

  // Crear solicitud de acceso
  await fetch('/api/firebase-auth/request-access-google', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: result.user.email,
      name: name,
      googleUid: result.user.uid
    })
  });

  // Hacer logout hasta que sea aprobado
  await signOut(auth);
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Observer del estado de autenticación
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

/**
 * Obtener token para peticiones al backend
 */
export async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}
